import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { RegisterRequest } from '../../types/api';
import { Form, FormField, FormLabel, Button } from '../ui';
import {
  FormInput,
  FormSelect,
  FormCpfCnpj,
  FormPhone,
  FormMail,
  FormCEP,
} from '../ui/FormControls';
import { messageHandler } from '../../services/message-handler.service';
import { subscriptionService } from '../../services';
import type { SubscriptionPlan } from '../../types/subscription';

interface AffiliateInfo {
  success: boolean;
  data: {
    name: string;
    affiliate_discount: number;
  };
}

interface RegisterFormProps {
  onSubmit: (data: RegisterRequest) => void;
  isLoading: boolean;
  affiliateCode?: string | null;
  affiliateDiscount?: number;
  inputAffiliateCode: string;
  onInputAffiliateCodeChange: (value: string) => void;
  onApplyAffiliateCode: () => void;
  isApplyingCode: boolean;
  isAffiliateError?: boolean;
  affiliateInfo?: AffiliateInfo | null;
}

function RegisterForm({
  onSubmit,
  isLoading,
  affiliateCode,
  affiliateDiscount,
  inputAffiliateCode,
  onInputAffiliateCodeChange,
  onApplyAffiliateCode,
  isApplyingCode,
  isAffiliateError = false,
  affiliateInfo = null,
}: RegisterFormProps) {
  const [formData, setFormData] = useState<RegisterRequest>({
    username: '',
    password: '',
    confirmPassword: '',
    person_type: 'fisica',
    cnpj_cpf: '',
    name: '',
    address: '',
    number: '',
    district: '',
    city: '',
    zip_code: '',
    phone: '',
    email: '',
    profile_id: 1, // Setting profile_id to 1 as required
  });

  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);

  // Fetch subscription plans with affiliate code if available
  const { data: plans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['subscription-plans', affiliateCode],
    queryFn: () => subscriptionService.getPlans(affiliateCode || undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    // Password validation - require letters, numbers, and special characters
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

    if (!passwordRegex.test(formData.password)) {
      messageHandler.warning(
        'A senha deve conter pelo menos 8 caracteres, incluindo letras, números e um caractere especial'
      );
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      messageHandler.warning('As senhas não coincidem');
      return false;
    }

    if (!selectedPlan) {
      messageHandler.warning('Por favor, selecione um plano');
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const { confirmPassword, ...submitData } = formData;
    onSubmit({
      ...submitData,
      plan_id: selectedPlan!,
      affiliate_code: affiliateCode || undefined,
    });
  };

  const formatCurrency = (value: string | number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(typeof value === 'string' ? parseFloat(value) : value);
  };

  // Calculate discounted price if affiliate discount is available
  const getDisplayPrice = (plan: SubscriptionPlan) => {
    // If the plan already has a discounted_price property from the API, use that
    if ('discounted_price' in plan) {
      return {
        original: formatCurrency(plan.price),
        discounted: formatCurrency(plan.discounted_price as number),
        hasDiscount: true,
      };
    }

    // Otherwise calculate it based on the affiliate_discount we have
    if (affiliateDiscount && affiliateDiscount > 0) {
      const discountAmount = (plan.price * affiliateDiscount) / 100;
      const discountedPrice = plan.price - discountAmount;
      return {
        original: formatCurrency(plan.price),
        discounted: formatCurrency(discountedPrice),
        hasDiscount: true,
      };
    }

    // No discount
    return {
      original: formatCurrency(plan.price),
      discounted: null,
      hasDiscount: false,
    };
  };

  return (
    <Form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Informações Pessoais
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField>
                <FormLabel htmlFor="person_type" required>
                  Tipo de Pessoa
                </FormLabel>
                <FormSelect
                  id="person_type"
                  name="person_type"
                  value={formData.person_type}
                  onChange={handleChange}
                  required
                >
                  <option value="fisica">Pessoa Física</option>
                  <option value="juridica">Pessoa Jurídica</option>
                </FormSelect>
              </FormField>

              <FormField>
                <FormLabel htmlFor="cnpj_cpf" required>
                  {formData.person_type === 'juridica' ? 'CNPJ' : 'CPF'}
                </FormLabel>
                <FormCpfCnpj
                  id="cnpj_cpf"
                  name="cnpj_cpf"
                  value={formData.cnpj_cpf}
                  onChange={handleChange}
                  type={formData.person_type === 'juridica' ? 'cnpj' : 'cpf'}
                  required
                />
              </FormField>
            </div>
          </div>

          <div className="lg:col-span-3">
            <FormField>
              <FormLabel htmlFor="name" required>
                {formData.person_type === 'juridica' ? 'Razão Social' : 'Nome Completo'}
              </FormLabel>
              <FormInput
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="email" required>
              Email
            </FormLabel>
            <FormMail
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="phone">Telefone</FormLabel>
            <FormPhone id="phone" name="phone" value={formData.phone} onChange={handleChange} />
          </FormField>

          <FormField>
            <FormLabel htmlFor="zip_code">CEP</FormLabel>
            <FormCEP
              id="zip_code"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
            />
          </FormField>
        </div>
      </div>

      {/* Address Information */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Endereço</h3>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3">
            <FormField>
              <FormLabel htmlFor="address">Endereço</FormLabel>
              <FormInput
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="number">Número</FormLabel>
            <FormInput id="number" name="number" value={formData.number} onChange={handleChange} />
          </FormField>

          <FormField>
            <FormLabel htmlFor="district">Bairro</FormLabel>
            <FormInput
              id="district"
              name="district"
              value={formData.district}
              onChange={handleChange}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="city">Cidade</FormLabel>
            <FormInput id="city" name="city" value={formData.city} onChange={handleChange} />
          </FormField>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Dados de Acesso
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <FormField>
            <FormLabel htmlFor="username" required>
              Usuário
            </FormLabel>
            <FormInput
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="password" required>
              Senha
            </FormLabel>
            <FormInput
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              minLength={8}
              required
              title="A senha deve conter pelo menos 8 caracteres, incluindo letras, números e um caractere especial"
            />
            <p className="mt-1 text-xs text-gray-500">
              Mínimo 8 caracteres, com letras, números e caracteres especiais
            </p>
          </FormField>

          <FormField>
            <FormLabel htmlFor="confirmPassword" required>
              Confirmar Senha
            </FormLabel>
            <FormInput
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </FormField>
        </div>
      </div>

      {/* Affiliate Code Section */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Código de Afiliado (opcional)
        </h3>

        <div className="flex flex-col md:flex-row gap-4">
          <FormField className="flex-1">
            <FormLabel htmlFor="affiliate_code">Tem um código de afiliado?</FormLabel>
            <div className="flex gap-2">
              <FormInput
                id="affiliate_code"
                name="affiliate_code"
                placeholder="Insira o código de afiliado para obter desconto"
                value={inputAffiliateCode || ''}
                onChange={e => onInputAffiliateCodeChange(e.target.value)}
                className="flex-1"
              />
              <Button type="button" onClick={onApplyAffiliateCode} isLoading={isApplyingCode}>
                Aplicar
              </Button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Se você foi convidado por alguém, insira o código de afiliado para obter desconto.
            </p>
          </FormField>
        </div>

        {affiliateCode && affiliateInfo && affiliateInfo.success && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">
                Código de afiliado aplicado! Você foi convidado por{' '}
                <strong>{affiliateInfo.data.name}</strong>
                {affiliateInfo.data.affiliate_discount > 0 && (
                  <span>
                    {' '}
                    e receberá <strong>{affiliateInfo.data.affiliate_discount}%</strong> de
                    desconto.
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {isAffiliateError && affiliateCode && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">
              Código de afiliado inválido. Por favor, verifique e tente novamente.
            </p>
          </div>
        )}
      </div>

      {/* Subscription Plans */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Plano de Assinatura
          {affiliateCode && affiliateDiscount && affiliateDiscount > 0 && (
            <span className="ml-2 text-sm font-normal text-green-600 dark:text-green-400">
              (Com {affiliateDiscount}% de desconto)
            </span>
          )}
        </h3>

        <FormLabel required>Selecione um plano</FormLabel>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoadingPlans ? (
            <div className="text-center py-4 col-span-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Carregando planos...</p>
            </div>
          ) : (
            plans?.map((plan: SubscriptionPlan) => {
              const priceInfo = getDisplayPrice(plan);

              return (
                <label
                  key={plan.id}
                  className={`
                    relative flex flex-col p-5 rounded-lg border-2 cursor-pointer transition-all h-full
                    ${
                      selectedPlan === plan.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                        : 'border-gray-200 hover:border-primary-300 dark:border-gray-700'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="plan_id"
                    value={plan.id}
                    checked={selectedPlan === plan.id}
                    onChange={() => setSelectedPlan(plan.id)}
                    className="sr-only"
                  />

                  {selectedPlan === plan.id && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle className="h-5 w-5 text-primary-600" />
                    </div>
                  )}

                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <h3 className="text-xl font-medium">{plan.name}</h3>
                      <p className="mt-2 text-sm text-gray-500">{plan.description}</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      {priceInfo.hasDiscount ? (
                        <div>
                          <p className="text-sm line-through text-gray-500">{priceInfo.original}</p>
                          <p className="text-2xl font-bold text-green-600">
                            {priceInfo.discounted}
                          </p>
                          <p className="text-sm text-gray-500">/mês</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-2xl font-bold">{priceInfo.original}</p>
                          <p className="text-sm text-gray-500">/mês</p>
                        </div>
                      )}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      <div className="flex flex-col items-center space-y-4 pt-4">
        <Button type="submit" isLoading={isLoading} className="w-full max-w-md py-3 text-lg">
          <ArrowRight className="h-5 w-5 mr-2" />
          Cadastrar
        </Button>

        <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-500">
          Já tem uma conta? Entre aqui
        </Link>
      </div>
    </Form>
  );
}

export default RegisterForm;
